import { useRouter, type UnknownOutputParams } from 'expo-router'
import { createContext, useContext, useEffect, useRef, useState, type PropsWithChildren, type ReactNode } from 'react'

import { env } from '@/src/config/env'
import { captureException } from '@/src/config/sentry'
import { DONATION_SOURCE } from '@/src/features/donation/api/types/donation'
import { useDonationBannerQuery } from '@/src/features/donation/services/useDonationBannerQuery'
import { useDimensions } from '@/src/general/hooks/useDimensions'
import { useSearchParamsAction } from '@/src/general/hooks/useSearchParamsAction'
import { axiosInstance } from '@/src/general/utilities/fetcher/axiosInstance'
import { isIOSWebview as getIsIOSWebview } from '@/src/general/utils/isIOSWebview'
import { loadVwoForVariant } from '@/src/general/utils/vwo/loadVwoForVariant'

import { useDonationBannerDisplayedMutation } from '../services/useDonationBannerDisplayedMutation'
import { AnimatedDonationBanner } from '../ui/components/AnimatedDonationBanner'
import type { TDonationBannerProps } from '../ui/components/DonationBanner/DonationBanner.types'
import { donationBannerPersistence } from '../utils/donationBannerPersistence'

const FUNDRAISEUP_PROVIDER = 'fundraiseup'

type TDonationContext = {
  openDonationBanner: (source: DONATION_SOURCE) => void
}

const DonationContext = createContext<TDonationContext>({
  openDonationBanner: () => {
    throw new Error(
      'Cannot set show donation banner: donation context is not provided. Seems you forgot to wrap component within <DonationProvider />',
    )
  },
})

type TDonationProviderProps = {
  searchParams: UnknownOutputParams
  disabled?: boolean
}

/**
 * DonationContext Provider
 *
 * Provides the donation state and source
 */
export const DonationProvider = ({
  children,
  searchParams,
  disabled = false,
}: PropsWithChildren<TDonationProviderProps>): ReactNode => {
  const [showDonationBanner, setShowDonationBanner] = useState<boolean>(false)
  const donationSourceRef = useRef(DONATION_SOURCE.AUTOMATIC)
  const { lessThan } = useDimensions()
  const router = useRouter()

  const { data: donationData, isPending, error } = useDonationBannerQuery()
  const { mutate: donationBannerDisplayedMutation } = useDonationBannerDisplayedMutation()

  if (donationData && donationData.user) {
    const segment = donationData.user.siteSegment
    if (segment === 1 || segment === 2) {
      loadVwoForVariant(segment)
    }
  }

  useSearchParamsAction({
    searchParams,
    action: ({ source }) => {
      if (env.EXPO_PUBLIC_BLOCK_BANNER) return
      setShowDonationBanner(true)
      // `source` arrives from the URL query (string | string[] | undefined); any non-empty string is treated verbatim as the donation source.
      let nextSource = DONATION_SOURCE.UNDEFINED
      if (typeof source === 'string' && source) {
        // oxlint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- query-param boundary
        nextSource = source as DONATION_SOURCE
      }
      donationSourceRef.current = nextSource
    },
    requestedQuery: { action: 'show-donation-banner', source: () => true },
  })

  useEffect(() => {
    const openBannerWithDelay = ({ informBackend }: { informBackend: boolean }) => {
      const timeoutId = setTimeout(() => {
        setShowDonationBanner(true)
        if (informBackend && donationSourceRef.current === DONATION_SOURCE.AUTOMATIC) {
          donationBannerDisplayedMutation({ source: donationSourceRef.current })
        }
      }, 3000)

      return () => clearTimeout(timeoutId)
    }

    if (env.EXPO_PUBLIC_BLOCK_BANNER) return

    // INFO: the banner's visibility is ultimately determined by the backend
    // Even if the banner is already displayed, we still reset persistence info and call banner-displayed
    if (donationData?.showBanner) {
      donationBannerPersistence.save()
      return openBannerWithDelay({ informBackend: true })
    }

    if (donationBannerPersistence.isActive()) {
      return openBannerWithDelay({ informBackend: false })
    }

    donationBannerPersistence.clear()
  }, [donationData?.showBanner, donationBannerDisplayedMutation])

  const isIOSWebview = getIsIOSWebview()

  const goToDonatePage: TDonationBannerProps['goToDonatePage'] = () => {
    const source = donationSourceRef.current

    const utmContent = {
      mobile: 'mobile_f5',
      desktop: 'desktop_f5',
    }

    router.navigate({
      pathname: `${axiosInstance.defaults.baseURL}/donate/auto`,
      params: {
        utm_medium: source === DONATION_SOURCE.AUTOMATIC ? 'cta_donation_campaign' : `${source}_Hozana_v2`,
        utm_content: lessThan.xm ? utmContent.mobile : utmContent.desktop,
        ...(isIOSWebview ? { provider: FUNDRAISEUP_PROVIDER } : {}),
        ...donationData?.link,
      },
    })
  }

  const openDonationBanner = (source: DONATION_SOURCE) => {
    donationSourceRef.current = source
    if (donationData) {
      setShowDonationBanner(true)
    } else {
      captureException.then((f) =>
        f(
          new Error(
            `[DonationContext] Unable to open donation banner: no donation banner info (${isPending ? 'pending' : 'not pending'}).`,
            { cause: error },
          ),
          {
            originalException: error,
          },
        ),
      )
      goToDonatePage()
    }
  }

  const closeDonationBanner = () => {
    setShowDonationBanner(false)
    donationBannerPersistence.clear()
  }

  return (
    <DonationContext.Provider value={{ openDonationBanner }}>
      <AnimatedDonationBanner
        showDonationBanner={showDonationBanner && !disabled}
        donationData={donationData}
        donationSourceRef={donationSourceRef}
        goToDonatePage={goToDonatePage}
        closeDonationBanner={closeDonationBanner}
      />
      {children}
    </DonationContext.Provider>
  )
}

export const useDonationContext = () => useContext(DonationContext)
